import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth, useCart } from '../App';
import { Button } from '../components/ui/button';
import { ShoppingCart, User, Menu, X, LogOut, Store, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function Header() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <span className="text-white font-heading font-bold text-lg">CK</span>
            </div>
            <span className="font-heading font-semibold text-lg text-foreground hidden sm:block">
              Christ Kingdom
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/products" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
              data-testid="nav-products"
            >
              Shop All
            </Link>
            <Link 
              to="/products?category=Fashion%20%26%20Apparel" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Fashion
            </Link>
            <Link 
              to="/products?category=Real%20Estate%20Services" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Real Estate
            </Link>
            <Link 
              to="/products?category=Oil%20%26%20Gas" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Oil & Gas
            </Link>
            <Link 
              to="/products?category=Gold%20%26%20Precious%20Metals" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Gold
            </Link>
            <Link 
              to="/products?category=Jewelry%20%26%20Accessories" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Jewelry
            </Link>
            <Link 
              to="/products?category=Shoes%20%26%20Footwear" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Shoes
            </Link>
            <Link 
              to="/products?category=Beauty%20%26%20Skincare" 
              className="text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              Beauty
            </Link>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Cart */}
            {user && (
              <Link to="/cart" className="relative" data-testid="cart-button">
                <Button variant="ghost" size="icon" className="relative">
                  <ShoppingCart className="h-5 w-5" />
                  {cart.item_count > 0 && (
                    <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {cart.item_count}
                    </span>
                  )}
                </Button>
              </Link>
            )}

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu-button">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" data-testid="dashboard-link">
                      <User className="mr-2 h-4 w-4" />
                      My Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {(user.role === 'vendor' || user.role === 'admin') && (
                    <DropdownMenuItem asChild>
                      <Link to="/vendor" className="cursor-pointer" data-testid="vendor-dashboard-link">
                        <Store className="mr-2 h-4 w-4" />
                        Vendor Dashboard
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="admin-dashboard-link">
                        <Settings className="mr-2 h-4 w-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-button">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-button">Login</Button>
                </Link>
                <Link to="/register">
                  <Button data-testid="register-button">Get Started</Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border py-4">
            <nav className="flex flex-col gap-3">
              <Link to="/products" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Shop All
              </Link>
              <Link to="/products?category=Fashion%20%26%20Apparel" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Fashion & Apparel
              </Link>
              <Link to="/products?category=Shoes%20%26%20Footwear" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Shoes
              </Link>
              <Link to="/products?category=Bags%20%26%20Accessories" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Bags & Accessories
              </Link>
              <Link to="/products?category=Beauty%20%26%20Skincare" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Beauty
              </Link>
              <Link to="/products?category=Gold%20%26%20Precious%20Metals" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Gold & Jewelry
              </Link>
              <Link to="/products?category=Real%20Estate%20Services" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Real Estate
              </Link>
              <Link to="/products?category=Oil%20%26%20Gas" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Oil & Gas
              </Link>
              <Link to="/products?category=Agriculture%20%26%20Farming" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Agriculture
              </Link>
              <Link to="/products?category=Construction%20%26%20Building" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Construction
              </Link>
              <Link to="/products?category=Auto%20%26%20Transportation" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Auto & Transport
              </Link>
              <Link to="/products?category=Professional%20Services" className="text-muted-foreground hover:text-foreground transition-colors font-medium py-2">
                Services
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
